import { UserProfile, Quest, Achievement } from '../types';
import { trackEvent } from './analyticsService';
import { achievementService } from './achievementService';

export interface DemoUser {
  profile: UserProfile;
  quests: Quest[];
  passiveSkills?: any[];
  isDemo: boolean;
  demoExpiresAt: number;
}

export const demoService = {
  // Create a demo user with instant XP and sample quests
  createDemoUser(): DemoUser {
    const now = Date.now();
    const demoExpiresAt = now + (6 * 60 * 60 * 1000); // 6 hours from now

    // Create demo profile with E-rank level 1 but engaging demo quests
    const demoProfile: UserProfile = {
      id: `demo_${now}`,
      name: 'Demo Hunter',
      dateOfBirth: undefined,
      level: 1, // Start at level 1 like new users
      currentXP: 0,
      xpToNextLevel: 100,
      totalXP: 0,
      currentTitle: 'Civilian', // Start as Civilian like new users
      stats: {
        strength: 10,
        intelligence: 10,
        agility: 10,
        vitality: 10,
        sense: 10,
      },
      passiveStatBoosts: {
        strength: 0,
        intelligence: 0,
        agility: 0,
        vitality: 0,
        sense: 0,
      },
      unallocatedStatPoints: 0, // No stat points like new users
      createdAt: now,
      streak: 0, // No streak like new users
      lastActivityDate: new Date().toISOString().split('T')[0],
      lastLoginDate: '', // Empty string like new users (triggers first login reward)
      lastPenaltyCheck: '', // Empty string like new users
      longestStreak: 0,
      currency: 100, // Starting currency like new users
      inventory: [],
      pomodoroSessions: 0,
      totalFocusTime: 0,
      penaltyActive: false,
      penaltyQuestIds: [],
      subscription: {
        tier: 'trial',
        status: 'trialing',
        createdAt: now,
        trialEndsAt: demoExpiresAt,
        currentPeriodEnd: 0,
        previousTier: 'trial',
        cancelAtPeriodEnd: false,
        updatedAt: now,
      },
      aiUsage: {},
      lifetimeStats: {
        totalQuestsCompleted: 0,
        totalXPEarned: 0,
        totalCurrencyEarned: 0,
        questsByDifficulty: {
          easy: 0,
          normal: 0,
          hard: 0,
          boss: 0,
        },
        questsByType: {
          daily: 0,
          main: 0,
          instant: 0,
          boss: 0,
        },
      },
      legalAgreements: undefined,
      backgroundSettings: {
        type: 'preset',
        presetId: 'default_dark',
        overlayOpacity: 30,
        blurIntensity: 0,
        particleEffect: 'none',
        particleIntensity: 50,
        parallaxEnabled: false,
        reducedQuality: false,
        customImageUrl: undefined,
        customImageName: undefined,
        videoUrl: undefined,
        videoName: undefined,
        gradient: { colors: ['#667eea', '#764ba2'], angle: 135 },
        solidColor: '#1a1a2e',
        imageCollection: [],
        videoCollection: [],
      },
    };

    // Create engaging demo quests that show the system's capabilities
    const demoQuests: Quest[] = [
      {
        id: `demo_quest_${now}_1`,
        userId: `demo_${now}`,
        title: 'Morning Routine',
        description: 'Complete your morning routine: exercise, meditation, and healthy breakfast',
        type: 'daily',
        difficulty: 'easy',
        xpReward: 25,
        currencyReward: 3,
        statRewards: { vitality: 2, strength: 1 },
        status: 'active',
        createdAt: now - 14400000, // Created 4 hours ago
        completedAt: null,
        resetDaily: false,
      },
      {
        id: `demo_quest_${now}_2`,
        userId: `demo_${now}`,
        title: 'Learn Something New',
        description: 'Spend 30 minutes learning a new skill or reading educational content',
        type: 'daily',
        difficulty: 'easy',
        xpReward: 20,
        currencyReward: 2,
        statRewards: { intelligence: 2 },
        status: 'active',
        createdAt: now - 18000000, // Created 5 hours ago
        completedAt: null,
        resetDaily: false,
      },
      {
        id: `demo_quest_${now}_3`,
        userId: `demo_${now}`,
        title: 'Social Connection',
        description: 'Have a meaningful conversation with a friend or family member',
        type: 'daily',
        difficulty: 'easy',
        xpReward: 15,
        currencyReward: 2,
        statRewards: { sense: 1 },
        status: 'active',
        createdAt: now - 21600000, // Created 6 hours ago
        completedAt: null,
        resetDaily: false,
      },
      {
        id: `demo_quest_${now}_4`,
        userId: `demo_${now}`,
        title: 'Network & Connect',
        description: 'Reach out to 3 professional contacts or attend a networking event',
        type: 'main',
        difficulty: 'normal',
        xpReward: 35,
        currencyReward: 4,
        statRewards: { sense: 2, intelligence: 1 },
        status: 'active',
        createdAt: now - 10800000, // Created 3 hours ago
        completedAt: null,
        resetDaily: false,
      },
      {
        id: `demo_quest_${now}_5`,
        userId: `demo_${now}`,
        title: 'Boss Quest: Complete Project',
        description: 'Finish your main project milestone - this is a high-value quest',
        type: 'boss',
        difficulty: 'boss',
        xpReward: 100,
        currencyReward: 15,
        statRewards: { strength: 3, intelligence: 3, sense: 2 },
        status: 'active',
        createdAt: now - 7200000, // Created 2 hours ago
        completedAt: null,
        resetDaily: false,
        scheduledFor: new Date().toISOString().split('T')[0],
      },
    ];

    // Track demo creation
    trackEvent('demo_user_created', {
      demo_level: demoProfile.level,
      demo_quests_count: demoQuests.length,
      demo_currency: demoProfile.currency,
    });

    // Create sample passive skills for demo
    const demoPassiveSkills = [
      {
        id: `demo_passive_${now}_1`,
        userId: `demo_${now}`,
        name: 'Morning Meditation',
        description: 'Daily 10-minute meditation practice',
        category: 'mental',
        icon: '🧘',
        frequency: 'daily',
        level: 0,
        currentStreak: 0,
        longestStreak: 0,
        totalCompletions: 0,
        statBoost: {
          stat: 'intelligence',
          percentBoost: 0 // 0% boost at level 0
        },
        createdAt: now - 86400000, // Created 1 day ago
        lastCompletedDate: '', // No completions yet
        completionHistory: [],
        isActive: true
      },
      {
        id: `demo_passive_${now}_2`,
        userId: `demo_${now}`,
        name: 'Strength Training',
        description: 'Regular strength training sessions',
        category: 'physical',
        icon: '💪',
        frequency: 'daily',
        level: 0,
        currentStreak: 0,
        longestStreak: 0,
        totalCompletions: 0,
        statBoost: {
          stat: 'strength',
          percentBoost: 0 // 0% boost at level 0
        },
        createdAt: now - 172800000, // Created 2 days ago
        lastCompletedDate: '', // No completions yet
        completionHistory: [],
        isActive: true
      },
      {
        id: `demo_passive_${now}_3`,
        userId: `demo_${now}`,
        name: 'Reading Habit',
        description: 'Read for 30 minutes daily',
        category: 'mental',
        icon: '📚',
        frequency: 'daily',
        level: 0,
        currentStreak: 0,
        longestStreak: 0,
        totalCompletions: 0,
        statBoost: {
          stat: 'intelligence',
          percentBoost: 0 // 0% boost at level 0
        },
        createdAt: now - 259200000, // Created 3 days ago
        lastCompletedDate: '', // No completions yet
        completionHistory: [],
        isActive: true
      },
      {
        id: `demo_passive_${now}_4`,
        userId: `demo_${now}`,
        name: 'Social Connection',
        description: 'Connect with friends or family daily',
        category: 'social',
        icon: '🤝',
        frequency: 'daily',
        level: 0,
        currentStreak: 0,
        longestStreak: 0,
        totalCompletions: 0,
        statBoost: {
          stat: 'sense',
          percentBoost: 0 // 0% boost at level 0
        },
        createdAt: now - 345600000, // Created 4 days ago
        lastCompletedDate: '', // No completions yet
        completionHistory: [],
        isActive: true
      },
      {
        id: `demo_passive_${now}_5`,
        userId: `demo_${now}`,
        name: 'Productivity Focus',
        description: 'Complete deep work sessions',
        category: 'productivity',
        icon: '⚡',
        frequency: 'daily',
        level: 0,
        currentStreak: 0,
        longestStreak: 0,
        totalCompletions: 0,
        statBoost: {
          stat: 'agility',
          percentBoost: 0 // 0% boost at level 0
        },
        createdAt: now - 432000000, // Created 5 days ago
        lastCompletedDate: '', // No completions yet
        completionHistory: [],
        isActive: true
      }
    ];

    return {
      profile: demoProfile,
      quests: demoQuests,
      passiveSkills: demoPassiveSkills,
      isDemo: true,
      demoExpiresAt,
    };
  },

  // Check if current user is a demo user
  isDemoUser(userId: string): boolean {
    return userId.startsWith('demo_');
  },

  // Get demo expiration time
  getDemoExpiration(userId: string): number | null {
    if (!this.isDemoUser(userId)) return null;
    
    const demoData = localStorage.getItem(`demo_data_${userId}`);
    if (demoData) {
      const parsed = JSON.parse(demoData);
      return parsed.demoExpiresAt;
    }
    return null;
  },

  // Check if demo has expired
  isDemoExpired(userId: string): boolean {
    const expiration = this.getDemoExpiration(userId);
    if (!expiration) return false;
    return Date.now() > expiration;
  },

  // Save demo data to localStorage
  saveDemoData(demoUser: DemoUser): void {
    const key = `demo_data_${demoUser.profile.id}`;
    localStorage.setItem(key, JSON.stringify(demoUser));
    // Store demo user ID in session storage for transfer
    sessionStorage.setItem('demo_user_id', demoUser.profile.id);
  },

  // Load demo data from localStorage
  loadDemoData(userId: string): DemoUser | null {
    const key = `demo_data_${userId}`;
    const data = localStorage.getItem(key);
    if (data) {
      return JSON.parse(data);
    }
    return null;
  },

  // Clear demo data
  clearDemoData(userId: string): void {
    const key = `demo_data_${userId}`;
    localStorage.removeItem(key);
  },


  // Helper to get title for level
  getTitleForLevel(level: number): string {
    if (level >= 100) return 'Shadow Monarch';
    if (level >= 80) return 'S-Rank Hunter';
    if (level >= 60) return 'A-Rank Hunter';
    if (level >= 40) return 'B-Rank Hunter';
    if (level >= 25) return 'C-Rank Hunter';
    if (level >= 15) return 'D-Rank Hunter';
    if (level >= 5) return 'E-Rank Hunter';
    return 'Civilian';
  },

  // Get demo progress (for showing in UI)
  getDemoProgress(demoUser: DemoUser): {
    questsCompleted: number;
    totalQuests: number;
    level: number;
    xpProgress: number;
    timeRemaining: string;
  } {
    const completedQuests = demoUser.quests.filter(q => q.status === 'completed').length;
    const totalQuests = demoUser.quests.length;
    const xpProgress = (demoUser.profile.currentXP / demoUser.profile.xpToNextLevel) * 100;
    
    const timeRemaining = Math.max(0, demoUser.demoExpiresAt - Date.now());
    const hoursRemaining = Math.floor(timeRemaining / (1000 * 60 * 60));
    const minutesRemaining = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
    
    return {
      questsCompleted: completedQuests,
      totalQuests,
      level: demoUser.profile.level,
      xpProgress,
      timeRemaining: `${hoursRemaining}h ${minutesRemaining}m`,
    };
  },

  // Track demo quest completion
  trackDemoQuestCompletion(questId: string, xpGained: number): void {
    trackEvent('demo_quest_completed', {
      quest_id: questId,
      xp_gained: xpGained,
      demo_session: true,
    });
  },


  // Track demo to signup conversion
  trackDemoToSignupConversion(): void {
    trackEvent('demo_to_signup_conversion', {
      conversion_source: 'demo_experience',
      demo_engagement: 'high',
    });
  },
};
